import { obtainAppNameByPid } from '@/comm/app'
import { useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownCountry1 } from '@/components/DropdownCountry'
import {
  DropdownPackageName,
  usePackageName,
} from '@/components/DropdownPackage'
import AppSelect from '@/components/zthd/app-select'
import { useUrlParamsStore } from '@/stores/url-params-store'
import MyDatePicker, { useDatePicker2 } from '@/components/MyDatePicker'
import { DatePicker } from '@/components/zthd/date-picker'
import MonthPicker from '@/components/zthd/MonthPicker'
import dayjs from 'dayjs'
import { getLastCompleteDate } from '@/lib/date-utils'

const RevenuesSnapshot = () => {
  const [mode, setMode] = useState('detail')
  // For Summary mode date selection
  const [summaryDateType, setSummaryDateType] = useState('daily') // 'daily', 'weekly', 'monthly', 'custom'
  const lastCompleteDate = getLastCompleteDate(true)
  const [summaryDate, setSummaryDate] = useState(
    dayjs(lastCompleteDate.toString()).toDate()
  )
  const [summaryWeek, setSummaryWeek] = useState(
    dayjs(lastCompleteDate.toString()).toDate()
  )
  const [summaryMonth, setSummaryMonth] = useState(dayjs(lastCompleteDate.toString()).startOf('month'))

  const { startDate, endDate, allowIncomplete } = useDatePicker2()
  const updateStartDate = useDatePicker2((state) => state.updateStartDate)
  const updateEndDate = useDatePicker2((state) => state.updateEndDate)
  const params = useUrlParamsStore((state) => state.params)
  const setParam = useUrlParamsStore((state) => state.setParam)
  const removeParam = useUrlParamsStore((state) => state.removeParam)
  const packageName = usePackageName((state) => state.value)
  const updatePackageName = usePackageName((state) => state.update)

  const pid = params.pid || '1'
  const country = params.country || 'BR'

  const queryParams = {
    mode,
    pid: mode === 'detail' ? pid : undefined,
    country: mode === 'detail' ? country : undefined,
  }

  // Handle date parameters based on mode
  if (mode === 'detail') {
    queryParams.startDate = dayjs(startDate).format('YYYYMMDD')
    queryParams.endDate = dayjs(endDate).format('YYYYMMDD')
  } else {
    // Summary mode
    if (summaryDateType === 'daily') {
      const formattedDate = dayjs(summaryDate).format('YYYYMMDD')
      queryParams.startDate = formattedDate
      queryParams.endDate = formattedDate
    } else if (summaryDateType === 'weekly') {
      // Assuming week starts on Monday
      const startOfWeek = dayjs(summaryWeek).startOf('week').add(1, 'day')
      const endOfWeek = dayjs(summaryWeek).endOf('week').add(1, 'day')
      queryParams.startDate = startOfWeek.format('YYYYMMDD')
      queryParams.endDate = endOfWeek.format('YYYYMMDD')
    } else if (summaryDateType === 'monthly') {
      // Monthly
      queryParams.startDate = summaryMonth
        .startOf('month').format('YYYYMMDD')
      queryParams.endDate = summaryMonth.endOf('month').format('YYYYMMDD')
    } else {
      queryParams.startDate = dayjs(startDate).format('YYYYMMDD')
      queryParams.endDate = dayjs(endDate).format('YYYYMMDD')
    }
  }

  if (mode === 'detail' && pid === '14' && packageName) {
    queryParams.package_name = packageName
  }

  const { data: response, isLoading } = useSWR([
    '/troas/revenue/reports',
    queryParams,
  ])
  const rawData = response?.data || []

  // Process data for detail mode: group by revenueDate, keep latest snapshot_date for each group
  const data = useMemo(() => {
    if (mode !== 'detail' || !rawData.length) return rawData

    // Filter by revenue_date = snapshot_date - 1
    const filtered = rawData.filter((item) => {
      const snapshotDate = dayjs(item.snapshotDate, 'YYYYMMDD')
      const revenueDate = dayjs(item.revenueDate, 'YYYYMMDD')
      const expectedRevenueDate = snapshotDate.subtract(1, 'day')
      
      return revenueDate.format('YYYYMMDD') === expectedRevenueDate.format('YYYYMMDD')
    })

    // Group by revenueDate + pid + country + packageName, keep latest snapshot_date
    const dataMap = new Map()
    
    filtered.forEach((item) => {
      const key = `${item.revenueDate}-${item.pid}-${item.country}-${item.packageName || ''}`
      const existing = dataMap.get(key)
      
      if (!existing || item.snapshotDate > existing.snapshotDate) {
        dataMap.set(key, item)
      }
    })

    // Convert to array and sort by revenueDate descending
    return Array.from(dataMap.values()).sort((a, b) => {
      // Sort by revenueDate descending (newest first)
      const revenueCompare = b.revenueDate.localeCompare(a.revenueDate)
      if (revenueCompare !== 0) return revenueCompare
      
      // Then by pid, country, packageName
      const pidCompare = Number(a.pid) - Number(b.pid)
      if (pidCompare !== 0) return pidCompare
      
      const countryCompare = a.country.localeCompare(b.country)
      if (countryCompare !== 0) return countryCompare
      
      return (a.packageName || '').localeCompare(b.packageName || '')
    })
  }, [mode, rawData])

  // Define country order
  const countryOrder = [
    'total',
    'BR',
    'JP',
    'MX',
    'DE',
    'US',
    'HK',
    'IT',
    'SA',
    'KR',
    'TW',
    'ES',
    'ZA',
    'ID',
    'GB',
    'FR',
    'TH',
    'AR',
    'CO',
    'SG',
    'MY',
    'CA',
    'AE',
  ]

  // Process data for matrix view when in aggregate mode
  const { uniquePids, uniqueCountries, dataMap } = useMemo(() => {
    if (mode !== 'aggregate' || !data.length)
      return { uniquePids: [], uniqueCountries: [], dataMap: {} }

    const pids = new Set()
    const countries = new Set()
    const map = {}

    data.forEach((item) => {
      pids.add(item.pid)
      let country = item.country
      if (!countryOrder.includes(country)) {
        country = 'Other'
      }
      countries.add(country)
      const key = `${item.pid}-${country}`

      if (!map[key]) {
        map[key] = { ...item, country }
        map[key].revSumIaaRevenue = Number(item.revSumIaaRevenue)
        map[key].revSumIapRevenue = Number(item.revSumIapRevenue)
        map[key].diffIaaRoas = Number(item.diffIaaRoas)
        map[key].diffIapRoas = Number(item.diffIapRoas)
      } else {
        map[key].revSumIaaRevenue += Number(item.revSumIaaRevenue)
        map[key].revSumIapRevenue += Number(item.revSumIapRevenue)
        map[key].diffIaaRoas += Number(item.diffIaaRoas)
        map[key].diffIapRoas += Number(item.diffIapRoas)
      }
    })

    const sortedCountries = Array.from(countries).sort((a, b) => {
      const indexA = countryOrder.indexOf(a)
      const indexB = countryOrder.indexOf(b)

      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })

    return {
      uniquePids: Array.from(pids).sort((a, b) => a - b),
      uniqueCountries: sortedCountries,
      dataMap: map,
    }
  }, [mode, data])

  const updateSearchParams = (key, value) => {
    if (value === 'total' || value === 'all') {
      removeParam(key)
    } else {
      setParam(key, value)
    }
  }

  const formatDiff = (diff, base, isPercent = true, isInteger = false) => {
    const diffNum = Number(diff)
    if (diffNum === 0) return '-'

    let percentVal = 0
    let percentStr = '0.00'

    if (base && Number(base) !== 0) {
      percentVal = (diffNum / Number(base)) * 100
      percentStr = percentVal.toFixed(2)
    }

    const isHighDiff = Math.abs(percentVal) > 5

    return (
      <div className="flex flex-col items-start">
        <span
          className={
            isHighDiff ? 'text-red-600 font-bold' : 'text-green-600 font-bold'
          }
        >
          {isPercent ? `${percentStr}%` : diffNum.toLocaleString()}
        </span>
        <span className="text-xs text-gray-400">
          {isPercent
            ? isInteger
              ? diffNum.toLocaleString()
              : diffNum.toFixed(2)
            : ''}
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Mode:</span>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="detail">Daily</SelectItem>
              <SelectItem value="aggregate">Summary</SelectItem>
            </SelectContent>
          </Select>
        </div>


        {mode === 'detail' ? (
          <MyDatePicker
            datePickerStore={useDatePicker2}
            getMaxDate="roas-complete"
            showMaxDate={true}
            defaultAllowIncomplete={false}
          />
        ) : (
          <div className="flex items-center gap-2">
            <Select value={summaryDateType} onValueChange={setSummaryDateType}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Date Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>

            {summaryDateType === 'daily' && (
              <DatePicker date={summaryDate} onSelect={setSummaryDate} />
            )}

            {summaryDateType === 'weekly' && (
              <div className="flex items-center gap-2">
                <DatePicker
                  date={summaryWeek}
                  onSelect={setSummaryWeek}
                  placeholder="Pick a week"
                />
                <span className="text-xs text-gray-500">
                  {dayjs(summaryWeek).startOf('week').add(1, 'day').format('MM-DD')}
                  {' ~ '}
                  {dayjs(summaryWeek).endOf('week').add(1, 'day').format('MM-DD')}
                </span>
              </div>
            )}

            {summaryDateType === 'monthly' && (
              <MonthPicker
                currentMonth={summaryMonth.toDate()}
                onMonthChange={(date) => setSummaryMonth(dayjs(date))}
              />
            )}

            {summaryDateType === 'custom' && (
              <MyDatePicker
                datePickerStore={useDatePicker2}
                getMaxDate="roas-complete"
                showMaxDate={true}
                defaultAllowIncomplete={false}
              />
            )}
          </div>
        )}

        {mode === 'detail' && (
          <>
            <AppSelect
              value={pid}
              onValueChange={(value) => updateSearchParams('pid', value)}
            />
            {pid === '14' && (
              <DropdownPackageName value={packageName} update={updatePackageName} />
            )}
            <DropdownCountry1
              value={country}
              update={(value) => updateSearchParams('country', value)}
            />
          </>
        )}

      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {mode === 'detail' ? 'Daily Detail Report' : 'Summary Aggregation'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              {mode === 'detail' ? (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                    <TableRow>
                      <TableHead>Snapshot Date</TableHead>
                      <TableHead>Revenue Date</TableHead>
                      <TableHead>PID</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Package</TableHead>

                      <TableHead className="bg-gray-100">Sum IAA</TableHead>
                      <TableHead className="bg-gray-100">Sum IAP</TableHead>

                      <TableHead className="bg-blue-50">ROAS IAA</TableHead>
                      <TableHead className="bg-blue-50">ROAS IAP</TableHead>
                      <TableHead className="bg-blue-50">ROAS Imp</TableHead>

                      <TableHead className="bg-green-50">AdMob IAA</TableHead>
                      <TableHead className="bg-green-50">AdMob Imp</TableHead>

                      <TableHead className="text-red-600">
                        <div>Diff IAA (ROAS)</div>
                        <div className="text-[10px] opacity-70">
                          (Sum-Roas)/Sum
                        </div>
                      </TableHead>
                      <TableHead className="text-red-600">
                        <div>Diff IAP (ROAS)</div>
                        <div className="text-[10px] opacity-70">
                          (Sum-Roas)/Sum
                        </div>
                      </TableHead>
                      <TableHead className="text-red-600">
                        <div>Diff IAA (AdMob)</div>
                        <div className="text-[10px] opacity-70">
                          (Sum-AdMob)/Sum
                        </div>
                      </TableHead>
                      <TableHead className="text-red-600">
                        <div>Diff Imp</div>
                        <div className="text-[10px] opacity-70">
                          (AdMob-Roas)/AdMob
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.snapshotDate}</TableCell>
                        <TableCell>{row.revenueDate}</TableCell>
                        <TableCell>{row.pid}</TableCell>
                        <TableCell>{row.country}</TableCell>
                        <TableCell>{row.packageName}</TableCell>

                        <TableCell className="bg-gray-100">
                          {Number(row.revSumIaaRevenue).toFixed(2)}
                        </TableCell>
                        <TableCell className="bg-gray-100">
                          {Number(row.revSumIapRevenue).toFixed(2)}
                        </TableCell>

                        <TableCell className="bg-blue-50">
                          {Number(row.roasIaaRevenue).toFixed(2)}
                        </TableCell>
                        <TableCell className="bg-blue-50">
                          {Number(row.roasIapRevenue).toFixed(2)}
                        </TableCell>
                        <TableCell className="bg-blue-50">
                          {Number(row.roasImpressions).toLocaleString()}
                        </TableCell>

                        <TableCell className="bg-green-50">
                          {Number(row.admobIaaRevenue).toFixed(2)}
                        </TableCell>
                        <TableCell className="bg-green-50">
                          {Number(row.admobImpressions).toLocaleString()}
                        </TableCell>

                        <TableCell>
                          {formatDiff(row.diffIaaRoas, row.revSumIaaRevenue)}
                        </TableCell>
                        <TableCell>
                          {formatDiff(row.diffIapRoas, row.revSumIapRevenue)}
                        </TableCell>
                        <TableCell>
                          {formatDiff(row.diffIaaAdmob, row.revSumIaaRevenue)}
                        </TableCell>
                        <TableCell>
                          {formatDiff(
                            -Number(row.diffImp),
                            row.admobImpressions,
                            true,
                            true
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                    <TableRow>
                      <TableHead className="w-[100px] bg-background">
                        Country
                      </TableHead>
                      {uniquePids.map((pid) => (
                        <TableHead
                          key={pid}
                          className="text-center bg-background"
                        >
                          <div>{obtainAppNameByPid(pid) || pid}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uniqueCountries.map((country) => (
                      <TableRow key={country}>
                        <TableCell className="font-medium bg-background sticky left-0 z-10">
                          {country}
                        </TableCell>
                        {uniquePids.map((pid) => {
                          const item = dataMap[`${pid}-${country}`]
                          return (
                            <TableCell key={pid} className="text-center border-l">
                              {item ? (
                                <div className="flex justify-center gap-4">
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-500 uppercase">
                                      IAA
                                    </span>
                                    {formatDiff(
                                      item.diffIaaRoas,
                                      item.revSumIaaRevenue
                                    )}
                                  </div>
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-500 uppercase">
                                      IAP
                                    </span>
                                    {formatDiff(
                                      item.diffIapRoas,
                                      item.revSumIapRevenue
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default RevenuesSnapshot